import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import { newId } from '@/lib/id';
import { type XrayItem, xrayOutbox } from '@/lib/outbox';
import type { User, Xray } from '@/types/api';
import type { CompressedImage } from '@/utils/image';
import { failureKind } from '../../sync/syncOutbox';
import { xraysApi } from '../api';

function toLocal(item: XrayItem): Xray {
  return {
    id: `local-${item.clientId}`,
    clientId: item.clientId,
    patientId: item.patientId,
    visitId: item.visitId,
    teeth: item.teeth,
    width: item.width,
    height: item.height,
    bytes: item.blob.size,
    takenAt: item.takenAt,
    createdBy: { id: item.authorId, name: item.authorName },
    createdAt: item.queuedAt,
    local: true,
    failed: item.failed,
  };
}

const newestFirst = (a: Xray, b: Xray) => b.takenAt.localeCompare(a.takenAt);

/**
 * The patient's X-rays: the server's, plus images still on this device. A new
 * image is queued on the device first (so a reload or a lost connection never
 * loses it), then sent at once; the background sync retries what is left.
 */
export function useChartXrays(patientId: string, user: User | null, dentistId: string | null) {
  const { t } = useTranslation();
  const toast = useToast();
  const server = useResource(() => xraysApi.list(patientId), patientId);
  const [queued, setQueued] = useState<XrayItem[]>([]);
  const { reload, setData } = server;
  const userId = user?.id ?? null;
  // Fetched images, kept for the page's lifetime (patient data: never in a persistent cache).
  const blobs = useRef(new Map<string, Promise<Blob>>());
  // Queued images deleted while their upload was under way: not shown again when it lands.
  const cancelled = useRef(new Set<string>());

  useEffect(() => {
    let alive = true;
    let previous = -1;
    const load = () =>
      xrayOutbox.forPatient(patientId).then((all) => {
        if (!alive) return;
        // Images another user queued on this device stay hidden until they sign in.
        const items = all.filter((i) => i.authorId === userId);
        setQueued(items);
        // Something left the queue: it reached the server.
        if (previous > items.length) void reload();
        previous = items.length;
      });
    void load();
    const off = xrayOutbox.subscribe(() => void load());
    return () => {
      alive = false;
      off();
    };
  }, [patientId, userId, reload]);

  const xrays = useMemo(() => {
    const list = [...(server.data ?? [])];
    const seen = new Set(list.map((x) => x.clientId));
    for (const item of queued) if (!seen.has(item.clientId)) list.push(toLocal(item));
    return list.sort(newestFirst);
  }, [server.data, queued]);

  const send = useCallback(
    async (item: XrayItem) => {
      try {
        const stored = await xraysApi.upload(item);
        await xrayOutbox.remove([item.clientId]);
        if (cancelled.current.has(item.clientId)) {
          // Deleted on the device meanwhile: delete what reached the server too.
          await xraysApi.remove(stored.id);
          return;
        }
        setData((list) => [...(list ?? []).filter((x) => x.clientId !== stored.clientId), stored]);
      } catch (err) {
        if (failureKind(err) === 'retry') {
          toast.show(t('chart.xrays.queued'));
          return;
        }
        await xrayOutbox.markFailed(item.clientId, errorMessage(err));
        toast.show(errorMessage(err), 'error');
      }
    },
    [setData, toast, t]
  );

  /** `visitId`: add to that visit (an earlier one) instead of the open visit of the day. */
  const add = useCallback(
    async (images: CompressedImage[], teeth: number[], visitId: string | null) => {
      if (!user) return;
      const now = new Date().toISOString();
      const items: XrayItem[] = images.map((image) => ({
        clientId: newId(),
        patientId,
        dentistId,
        visitId,
        teeth,
        blob: image.blob,
        width: image.width,
        height: image.height,
        takenAt: now,
        authorId: user.id,
        authorName: user.name,
        queuedAt: now,
      }));
      for (const item of items) await xrayOutbox.add(item);
      for (const item of items) await send(item);
    },
    [user, patientId, dentistId, send]
  );

  const remove = useCallback(
    async (xray: Xray) => {
      try {
        if (xray.local) {
          cancelled.current.add(xray.clientId);
          await xrayOutbox.remove([xray.clientId]);
        } else {
          await xraysApi.remove(xray.id);
          setData((list) => list?.filter((x) => x.id !== xray.id));
          blobs.current.delete(xray.id);
        }
        toast.show(t('chart.xrays.deleted'));
      } catch (err) {
        toast.show(errorMessage(err), 'error');
      }
    },
    [setData, toast, t]
  );

  /** The image of an X-ray: from the device while queued, otherwise fetched once. */
  const image = useCallback(
    (xray: Xray): Promise<Blob> => {
      if (xray.local) {
        const item = queued.find((i) => i.clientId === xray.clientId);
        return item ? Promise.resolve(item.blob) : Promise.reject(new Error('not queued'));
      }
      const cached = blobs.current.get(xray.id);
      if (cached) return cached;
      const loading = xraysApi.image(xray.id);
      blobs.current.set(xray.id, loading);
      // A failed load (offline) may be retried later.
      loading.catch(() => blobs.current.delete(xray.id));
      return loading;
    },
    [queued]
  );

  return { xrays, loading: server.loading, add, remove, image };
}
